import classNames from 'classnames';
import Clipboard from 'clipboard';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { CSSProperties, ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PopupProps } from 'semantic-ui-react';
import { Button, Label, Icon, Popup } from 'semantic-ui-react';

import raiseToast from '@components/toast';

import s from './copy-button.scss';

interface CopyButtonProps {
  content?: string | null; // CopyButton is disabled when value is null or undefined
  message: string | ReactElement;
  corner?: boolean;
  icon?: boolean;
  /**
   * When true, replaces the copy icon with a green checkmark
   * for 2 seconds on success instead of showing a toast notification.
   */
  inlineConfirmation?: boolean;
  className?: string;
  style?: CSSProperties;
  popupProps?: PopupProps;
}

const ToastMessage = ({ message }: { message: CopyButtonProps['message'] }) =>
  typeof message === 'string' ? (
    <span>
      {message.length
        ? message
        : 'This item has been successfully copied to your clipboard.'}
    </span>
  ) : (
    message
  );

export const CopyButton = ({
  content = null,
  message = '',
  corner = false,
  icon = false,
  inlineConfirmation = false,
  className,
  style,
  popupProps,
}: CopyButtonProps) => {
  useStyles(s);

  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleInlineCopy = useCallback(() => {
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1000);
  }, []);

  // Clear timer on unmount
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Use clipboard and on unmount, clean it up
  useEffect(() => {
    const clipboard = new Clipboard(`.${s.copy}`);
    return () => clipboard.destroy();
  }, []);

  return icon ? (
    <Popup
      position="top center"
      trigger={
        <Icon
          link
          color={copied ? 'green' : 'teal'}
          name={copied ? 'check circle' : 'copy'}
          className={classNames(s.copy, s.root, s.icon, className)}
          data-clipboard-text={content || ''}
          style={style}
          onClick={() => {
            if (inlineConfirmation) {
              handleInlineCopy();
            } else {
              raiseToast({
                message: <ToastMessage message={message} />,
                level: 'info',
                autoClose: 8000,
                icon: 'copy',
                dismissAll: true,
              });
            }
          }}
          disabled={!content}
          aria-disabled={!content}
        />
      }
      content={copied ? 'Copied!' : 'Copy to clipboard'}
      {...popupProps}
      {...(inlineConfirmation && copied ? { open: true } : {})}
      disabled={!content}
      aria-disabled={!content}
    />
  ) : (
    <Button
      as={Label}
      onClick={() => {
        raiseToast({
          message: <ToastMessage message={message} />,
          level: 'info',
          autoClose: 8000,
          icon: 'copy',
          dismissAll: true,
        });
      }}
      basic
      className={classNames(
        s.copy,
        s.root,
        corner ? `${s.corner} darkCornerFix` : '',
        className,
      )}
      data-clipboard-text={content || ''}
      style={{ cursor: 'pointer', ...style }}
      disabled={!content}
      aria-disabled={!content}
    >
      <span>
        <Icon color="teal" name="copy" />
        Copy
      </span>
    </Button>
  );
};

export default CopyButton;
